import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, ShieldCheck, Phone, Clock, CheckCircle2, ChevronDown, Sparkles, Star, Truck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QuoteCalculator from '../components/QuoteCalculator';
import TrustedBrokers from '../components/TrustedBrokers';
import FooterCTABand from '../components/FooterCTABand';
import { Button } from '../components/ui/button';
import { findStateBySlug } from '../data/statePages';
import { COMPANY, HERO_IMAGES } from '../mock';
import useSEO from '../hooks/useSEO';

const SITE = 'https://www.excelenceinc.com';

const Faq = ({ q, a, open, onClick, idx }) => (
  <div className="border border-[#E5EAF2] rounded-xl bg-white overflow-hidden">
    <button
      data-testid={`state-faq-${idx}`}
      onClick={onClick}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#F7F9FC] transition-colors"
    >
      <span className="font-bold text-[#0A1628] text-sm md:text-base">{q}</span>
      <ChevronDown className={`w-5 h-5 text-[#2AA8E8] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="px-5 pb-5 text-sm text-[#4A5A72] leading-relaxed border-t border-[#E5EAF2] pt-4">{a}</div>}
  </div>
);

const StateLandingPage = ({ slug }) => {
  const state = findStateBySlug(slug);
  const [openFaq, setOpenFaq] = useState(0);

  const title = state
    ? `Car Shipping ${state.name} | Auto Transport ${state.name} | Excelence Inc`
    : 'Car Shipping | Excelence Inc';
  const description = state
    ? `Professional car shipping and auto transport in ${state.name}. Open & enclosed transport, dealer logistics, instant quotes. Licensed (MC1766140 · DOT4474028). Nationwide coverage.`
    : 'Nationwide car shipping with Excelence Inc.';
  const canonical = `${SITE}/car-shipping-${slug}`;

  const jsonLd = state
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: `Car Shipping ${state.name}`,
          serviceType: 'Auto Transport',
          provider: { '@id': `${SITE}/#organization` },
          areaServed: { '@type': 'State', name: state.name },
          description: `Professional car shipping services in ${state.name} — open and enclosed auto transport, dealer logistics and nationwide delivery by Excelence Inc.`,
          url: canonical,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
            { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE}/services` },
            { '@type': 'ListItem', position: 3, name: `Car Shipping ${state.name}`, item: canonical },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: state.faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ]
    : [];

  useSEO({ title, description, canonical, jsonLd });

  if (!state) {
    return (
      <>
        <Header />
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center">
          <h1 className="text-3xl font-extrabold text-[#0A1628]">State page not found</h1>
          <p className="mt-3 text-[#4A5A72]">We couldn\u2019t find the page for "{slug}".</p>
          <Link to="/" className="mt-6 inline-block"><Button className="btn-accent">Return Home</Button></Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-[#0A1628]">
          <div className="absolute inset-0 brand-gradient-tech" />
          <div className="absolute inset-0 circuit-bg-dark opacity-50" />
          <div className="absolute inset-0 z-0">
            <img src={HERO_IMAGES.main} alt={`Excelence Inc auto transport in ${state.name}`} className="w-full h-full object-cover opacity-25 mix-blend-luminosity" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/90 via-[#0A1628]/75 to-[#0E2744]/60" />
          </div>
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2AA8E8]/20 blur-3xl z-0" />
          <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#D4A24C]/15 blur-3xl z-0" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-16 md:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3 text-white">
                <nav className="flex items-center gap-2 text-xs font-semibold text-white/60 mb-5" aria-label="Breadcrumb">
                  <Link to="/" className="hover:text-[#2AA8E8]">Home</Link>
                  <span>/</span>
                  <Link to="/services" className="hover:text-[#2AA8E8]">Services</Link>
                  <span>/</span>
                  <span className="text-white">Car Shipping {state.name}</span>
                </nav>

                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2AA8E8]/10 backdrop-blur-sm border border-[#2AA8E8]/30 text-xs font-semibold mb-6 text-[#2AA8E8]">
                  <MapPin className="w-3.5 h-3.5" />
                  Auto transport serving all of {state.name}
                </div>

                <h1 data-testid="state-hero-h1" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-5">
                  <span className="block">Car Shipping</span>
                  <span className="block text-[#2AA8E8]">{state.name}</span>
                  <span className="block gold-text text-3xl sm:text-4xl lg:text-5xl mt-1">Nationwide Auto Transport</span>
                </h1>

                <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
                  {state.intro}
                </p>

                <div className="flex flex-wrap gap-3 mb-8">
                  <a href="#quote">
                    <Button data-testid="state-hero-quote-btn" className="btn-accent font-bold h-12 px-6 gap-2">
                      <Sparkles className="w-4 h-4" /> Get Instant {state.name} Quote
                    </Button>
                  </a>
                  <a href={COMPANY.phoneHref}>
                    <Button variant="outline" className="h-12 px-6 border-white/30 text-white bg-white/5 hover:bg-white hover:text-[#0A1628] font-semibold gap-2 backdrop-blur-sm">
                      <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                    </Button>
                  </a>
                </div>

                {/* Trust strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 border-t border-white/10 pt-6">
                  {[
                    { i: ShieldCheck, v: 'Licensed', s: 'MC1766140 · DOT4474028' },
                    { i: Clock, v: '24–48 hrs', s: 'Avg pickup window' },
                    { i: Star, v: '4.9 / 5', s: '127+ reviews' },
                    { i: Truck, v: '20,000+', s: 'Vetted carriers' },
                  ].map((b, i) => {
                    const Icon = b.i;
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <Icon className="w-4 h-4 text-[#D4A24C] mt-0.5 shrink-0" />
                        <div>
                          <div className="text-sm font-extrabold text-white">{b.v}</div>
                          <div className="text-[11px] text-white/60 uppercase tracking-wider">{b.s}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quote calculator on the right */}
              <div id="quote" className="lg:col-span-2 scroll-mt-24">
                <QuoteCalculator />
              </div>
            </div>
          </div>
        </section>

        {/* CITIES */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#2AA8E8]">Local coverage</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0A1628] leading-tight">
                Car shipping to and from every major {state.name} city
              </h2>
              <p className="mt-4 text-base text-[#4A5A72] leading-relaxed">
                We serve every ZIP in {state.name}. Below are the {state.name} cities where we see the highest demand — pickup windows usually within 24–48 hours of booking.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {state.cities.map((c) => (
                <div
                  key={c}
                  data-testid={`state-city-${c.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="group flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[#E5EAF2] bg-[#F7F9FC] hover:bg-white hover:border-[#2AA8E8] hover:shadow-md transition-all"
                >
                  <MapPin className="w-4 h-4 text-[#2AA8E8] shrink-0" />
                  <span className="font-bold text-[#0A1628] text-sm md:text-base">Auto Transport {c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROUTES */}
        <section className="bg-[#F7F9FC] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4A24C]">Popular routes</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0A1628] leading-tight">
                Top {state.name} car shipping lanes
              </h2>
              <p className="mt-4 text-base text-[#4A5A72] leading-relaxed">
                These are our most-booked {state.name} routes. Each estimate is based on real dispatch data — actual pricing depends on vehicle type, trailer and timing.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {state.routes.map((r, i) => (
                <a
                  href="#quote"
                  key={i}
                  data-testid={`state-route-${i}`}
                  className="group bg-white rounded-xl border border-[#E5EAF2] p-5 hover:border-[#0A1628] hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-[#4A5A72] mb-3">
                    <span>Route #{i + 1}</span>
                    <span className="text-[#D4A24C]">{r.days} days</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#0A1628] font-extrabold text-lg">
                    <span>{r.from}</span>
                    <ArrowRight className="w-4 h-4 text-[#2AA8E8]" />
                    <span>{r.to}</span>
                  </div>
                  <div className="mt-2 text-xs text-[#4A5A72] font-semibold">{r.miles} · Open & enclosed available</div>
                  <div className="mt-4 text-sm font-bold text-[#0A1628] group-hover:text-[#2AA8E8] flex items-center gap-1.5">
                    Get a quote for this route <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* WHY US */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#2AA8E8]">Why Excelence Inc</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0A1628] leading-tight">
                Why {state.name} customers choose us
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { t: 'Fully Licensed', d: `FMCSA registered · MC1766140 · DOT4474028 · Fully insured on every ${state.name} shipment.` },
                { t: 'Fast Pickup', d: `Most ${state.name} pickups scheduled within 24–48 hours. Expedited same-day available.` },
                { t: 'Transparent Pricing', d: 'Instant estimator based on real distance, trailer type and vehicle class. No hidden fees.' },
                { t: 'Open + Enclosed', d: 'Every shipment matched to the right trailer. Enclosed for luxury, classic and EV.' },
              ].map((p, i) => (
                <div key={i} className="bg-[#F7F9FC] border border-[#E5EAF2] rounded-xl p-5 hover:border-[#0A1628] transition-colors">
                  <CheckCircle2 className="w-6 h-6 text-[#2AA8E8] mb-3" />
                  <div className="font-extrabold text-[#0A1628] mb-1.5">{p.t}</div>
                  <p className="text-sm text-[#4A5A72] leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#F7F9FC] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="mb-10 text-center">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4A24C]">Questions</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0A1628] leading-tight">
                {state.name} car shipping FAQ
              </h2>
            </div>
            <div className="space-y-3">
              {state.faqs.map((f, i) => (
                <Faq
                  key={i}
                  idx={i}
                  q={f.q}
                  a={f.a}
                  open={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </section>

        <TrustedBrokers />
        <FooterCTABand />
      </main>
      <Footer />
    </>
  );
};

export default StateLandingPage;
